import type { Nodes } from "mdast";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import stripMarkdown from "strip-markdown";
import { unified } from "unified";

const processor = unified().use(remarkParse).use(remarkGfm).use(stripMarkdown);

// The blocks arriving without terminal punctuation are the headings and list items.
const ENDS_A_SENTENCE = /[.!?:;,]$/;

/**
 * Flatten a markdown string to plain text for dense previews (cards, list rows).
 *
 * Parses GFM (matching how detail pages render) and strips all formatting —
 * headings, emphasis, strikethrough, code, lists, link urls (label kept), image
 * markup (alt text kept). Tables are dropped entirely, and no literal `#`, `*`,
 * `~`, or `|` survives. Render real markdown on detail pages, not here.
 *
 * Blocks are rejoined onto one line with a separator between them, so a heading
 * and the list items under it read as separate things.
 */
export const stripMarkdownToText = (markdown: string): string =>
  // Read text off the tree rather than re-serialising it, which would
  // backslash-escape every character markdown treats as syntax.
  processor
    .runSync(processor.parse(markdown))
    .children.map((block) => nodeText(block).replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .reduce(
      (text, block) =>
        text ? `${text}${ENDS_A_SENTENCE.test(text) ? " " : " · "}${block}` : block,
      ""
    );

/** The literal text under a node, with no markdown syntax or escaping. */
function nodeText(node: Nodes): string {
  if ("value" in node) {
    return node.value;
  }
  if ("children" in node) {
    return node.children.map(nodeText).join("");
  }
  return "";
}
